import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { FormCheckbox, FormHelperText, FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { AlertConfirm } from "components/notifications";
import { Tooltip } from "components/tooltip";
import { HelperFunctions } from "helpers";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import './Hierarchy.css';

export function HierarchyQuestionOptions({options, onChange, multipleCanBeSelectedByDefault, onValidChange, header, conditionsWhereThisIsTheTrigger, readOnly}) {
    const [error, setError] = useState("");
    const [indexOfOptionToRemove, setIndexOfOptionToRemove] = useState(null);

    useEffect(() => {
        if (!options?.length) {
            setError("At least one option is required.");
            return;
        }

        const optionNames = options.map(o => o.name?.trim()?.toLowerCase()) ?? [];

        if (optionNames.some(n => !n)) {
            setError("Name must be set on all options.");
            return;
        }

        const distinctNames = new Set(optionNames);

        if ([...distinctNames].length !== optionNames.length) {
            setError("Name must be unique for all options.");
            return;
        }

        setError("");
    }, [options]);
    
    useEffect(() => {
        if (!options || !options.length) {
            addOption()
        }
    }, [options]);

    useEffect(() => {
        onValidChange(!error)
    }, [error])

    const onNameChange = (index, newName) => {
        const newOptions = HelperFunctions.deepClone(options);
        newOptions[index].name = newName;
        onChange(newOptions);
    }

    const onSelectedByDefaultChange = (index) => {
        const newOptions = HelperFunctions.deepClone(options);
        newOptions[index].selectedByDefault = !newOptions[index].selectedByDefault;

        if (!multipleCanBeSelectedByDefault) {
            newOptions.forEach((e, i) => {
                if (i !== index) {
                    e.selectedByDefault = false;
                }
            });
        }

        onChange(newOptions);
    }

    const mapOptions = () => {
        if (!options) {
            return [];
        }

        return options.map((o, i) => {
            const mapResults = [
                readOnly ? <span key="name">{o.name}</span> : <FormTextInput key="name" value={o.name} onChange={(e) => onNameChange(i, e.target.value)}/>,
                <FormCheckbox key="default" disabled={readOnly} checked={o.selectedByDefault} onChange={() => onSelectedByDefaultChange(i)} />
            ];

            if (!readOnly) {
                mapResults.push(<div key="buttons" className="button-row-small">
                    <Tooltip content="Add">
                        <Button minimal={true} large={false} iconOnly={true} icon="plus" onClick={() => addOption(i)}/>
                    </Tooltip>
                    <Tooltip content="Remove">
                        <Button minimal={true} large={false} intent={Intent.DANGER} iconOnly={true} icon="trash" disabled={options?.length === 1} onClick={() => removeOption(i)}/>
                    </Tooltip>
                </div>);
            }

            return mapResults;
        });
    }

    const addOption = (index = 0) => {
        const newOptions = options ? [...options] : [];
        newOptions.splice(index + 1, 0, {
            id: crypto.randomUUID(),
            name: "",
            selectedByDefault: false
        });

        onChange(newOptions);
    }

    const removeOption = (index) => {
        if (optionUsedAsTriggerAnswer(index) && indexOfOptionToRemove == null) {
            setIndexOfOptionToRemove(index);
            return;
        } else {
            setIndexOfOptionToRemove(null);
        }

        const newOptions = options ? [...options] : [];
        newOptions.splice(index, 1);

        onChange(newOptions);
    }

    const optionUsedAsTriggerAnswer = (index) => {
        const option = options[index];
        return conditionsWhereThisIsTheTrigger.some(c => c.requiredAnswers.includes(option.id))
    }

    return <div className="form-field">
        <h4>{header}</h4>
        <ListingTable
            data={mapOptions()}
            pageable={false}
            headers={readOnly ? ["Name", "Default"] : ["Name", "Default", "Actions"]}
            loadingData={false}
            fitToContent={false}
            totalRecordCount={options?.length ?? 0}
            shrinkLastColumn={true}
        />
        <ShowHide
            evaluator={!!error}
            show={<FormHelperText danger={true}>{error}</FormHelperText>}
        />
        <AlertConfirm
            isOpen={indexOfOptionToRemove != null}
            onConfirm={() => removeOption(indexOfOptionToRemove)}
            title="Invalidate Condition"
            onCancel={() => setIndexOfOptionToRemove(null)}>
                <p>One or more conditions use this option to trigger whether the component should show or not. Are you sure you want to remove it? This will remove the option from all conditions where it is referenced.</p>
        </AlertConfirm>
    </div>
}

HierarchyQuestionOptions.propTypes = {
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    multipleCanBeSelectedByDefault: PropTypes.bool.isRequired,
    onValidChange: PropTypes.func.isRequired,
    header: PropTypes.string.isRequired,
    conditionsWhereThisIsTheTrigger: PropTypes.array.isRequired,
    readOnly: PropTypes.bool.isRequired
}